import { useQueries } from "@tanstack/react-query";
import { ATTRIBUTE_SOURCES } from "@/config/metrics";
import { clicksService } from "@/services/ClicksService";

type Loaded = { maps: (Map<string, unknown[]> | undefined)[]; failed: boolean[] };
// Module level, so useQueries keeps the combined result while nothing changed.
const combine = (results: { data?: Map<string, unknown[]>; isError: boolean }[]): Loaded => ({
  maps: results.map((r) => r.data),
  failed: results.map((r) => r.isError),
});

/**
 * The attribute dictionaries of ATTRIBUTE_SOURCES (Domain Tracker, ...), each
 * fetched only while one of its columns is shown and the rows have a domain.
 * The sources sync daily, so an hour is fresh enough. Not under the "clicks"
 * key: a refresh of the report must not fetch them again. A source missing on
 * the server (PGRST202, not deployed yet) is not retried and reads as failed.
 */
export function useAttributeSources(enabledIds: string[]) {
  const loaded = useQueries({
    queries: ATTRIBUTE_SOURCES.map((s) => ({
      queryKey: ["attributes", s.id],
      queryFn: () => clicksService.getAttributes(s.rpc),
      enabled: enabledIds.includes(s.id),
      staleTime: 60 * 60 * 1000,
      gcTime: 2 * 60 * 60 * 1000,
      retry: (n: number, error: unknown) => (error as { code?: string } | null)?.code !== "PGRST202" && n < 1,
      throwOnError: false,
    })),
    combine,
  });
  return {
    maps: loaded.maps,
    failedTitles: ATTRIBUTE_SOURCES.filter((s, i) => enabledIds.includes(s.id) && loaded.failed[i]).map((s) => s.title),
  };
}
